import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { projectsApi } from "../services/rag-service";
import { toast } from "sonner";
import { isKeycloakAuthenticated, keycloak } from "../services/keycloak";
import { useOrganization } from "./OrganizationContext";
import { setCookie, getCookie } from "../utils/security";
import { withAcceptLanguageHeader } from "../utils/requestLanguage";
import {
  getRequestScopeHeaders,
  setActiveProjectId,
} from "../utils/requestScope";
import i18n from "i18next";
import { useInvalidateProjectsQuery } from "../features/projects/hooks/useProjectsQuery";

const ProjectContext = createContext();

const STORAGE_KEY = "selectedProjects";
const SUPPORTED_PROJECT_ACCESS_LEVELS = new Set([
  "owner",
  "admin",
  "modifier",
  "viewer",
]);

const normalizeProjectAccessLevel = (accessLevel) =>
  SUPPORTED_PROJECT_ACCESS_LEVELS.has(accessLevel) ? accessLevel : null;

// Utility function to ensure we always have a valid selectedProjects array
export const validateSelectedProjects = (value) => {
  // Handle null, undefined, or invalid values
  if (!value || value === "undefined" || value === "null") {
    return ["all"];
  }

  // If it's already an array, validate it contains valid values
  if (Array.isArray(value)) {
    // Filter out any invalid entries and ensure we have at least one valid item
    const validEntries = value.filter(
      (item) => typeof item === "string" && item.trim().length > 0,
    );
    return validEntries.length > 0 ? validEntries : ["all"];
  }

  // If it's a string, try to parse it as JSON
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return validateSelectedProjects(parsed); // Recursive call to validate parsed result
    } catch (error) {
      return ["all"];
    }
  }

  // For any other type, return default
  return ["all"];
};

const getActiveProjectIdFromSelection = (selection) => {
  const validatedProjects = validateSelectedProjects(selection);
  return validatedProjects.length === 1 && validatedProjects[0] !== "all"
    ? validatedProjects[0]
    : null;
};

export const ProjectProvider = ({ children }) => {
  const invalidateProjectsQuery = useInvalidateProjectsQuery();
  // Initialize from localStorage if available, otherwise default to 'all'
  const [selectedProjects, setSelectedProjects] = useState(() => {
    try {
      const saved = getCookie(STORAGE_KEY);
      const validatedProjects = validateSelectedProjects(saved);
      setActiveProjectId(getActiveProjectIdFromSelection(validatedProjects));
      return validatedProjects;
    } catch (error) {
      setActiveProjectId(null);
      return ["all"];
    }
  });

  const [availableProjects, setAvailableProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  const isLatestRequest = (requestId) => requestId === requestIdRef.current;

  // Get organization context to listen for changes
  const { currentOrganization } = useOrganization();

  // Save to localStorage whenever selectedProjects changes
  useEffect(() => {
    try {
      // Validate and save selectedProjects
      const validatedProjects = validateSelectedProjects(selectedProjects);
      setCookie(STORAGE_KEY, JSON.stringify(validatedProjects));
      setActiveProjectId(getActiveProjectIdFromSelection(validatedProjects));

      // If the validated projects are different from current state, update state
      if (
        JSON.stringify(validatedProjects) !== JSON.stringify(selectedProjects)
      ) {
        setSelectedProjects(validatedProjects);
      }
    } catch (error) {
      // Silently handle localStorage save errors
    }
  }, [selectedProjects]);

  const fetchProjects = useCallback(async () => {
    const requestId = ++requestIdRef.current;

    try {
      console.log("Fetching projects for organization:", currentOrganization);
      setLoading(true);
      setError(null);

      try {
        // First attempt: use standard projectsApi
        let projects = [];
        try {
          projects = await projectsApi.getProjects({ includeShared: true });
        } catch (error) {
          console.error("Error fetching projects:", error);
        }

        // Transform the API response to match the expected format
        const formattedProjects = projects.map((project) => ({
          id: project.id,
          name: project.name,
          description: project.description || "",
          isOwner: project.is_owner,
          accessLevel: normalizeProjectAccessLevel(project.access_level),
          createdAt: project.created_at,
          apProjectId: project.ap_project_id,
          organizationId: project.organization_id,
          userId: project.user_id,
        }));

        if (!isLatestRequest(requestId)) {
          return;
        }

        setAvailableProjects(formattedProjects);

        // Reset selectedProjects to 'all' if the user had previously selected projects that no longer exist
        setSelectedProjects((currentSelected) => {
          if (!isLatestRequest(requestId)) {
            return currentSelected;
          }

          console.log({ currentSelected });
          if (formattedProjects.length === 0) {
            // If there are no projects, just select 'all'
            return ["all"];
          } else if (!currentSelected.includes("all")) {
            // For other selections, verify that the selected projects still exist
            const validProjectIds = formattedProjects.map((p) => p.id);
            console.log({ validProjectIds });
            const stillValidSelections = currentSelected.filter((id) =>
              validProjectIds.includes(id),
            );
            console.log({ stillValidSelections });
            if (stillValidSelections.length === 0) {
              // If none of the previously selected projects exist anymore, reset to 'all'
              return ["all"];
            }
          }
          return currentSelected; // No change needed
        });

        return;
      } catch (firstError) {
        // If standard API fails with 401, try direct fetch with explicit token handling

        // Only try fallback if it looks like an auth error
        if (
          firstError.message &&
          firstError.message.includes("Authentication")
        ) {
          // Import token functions if needed
          const {
            getKeycloakToken,
            isKeycloakAuthenticated: checkAuth,
            updateKeycloakToken,
          } = await import("../services/keycloak");

          // Check authentication
          if (!checkAuth()) {
            throw new Error("User is not authenticated");
          }

          // Force token refresh
          await updateKeycloakToken();

          const token = getKeycloakToken();
          if (!token) {
            throw new Error("No authentication token available");
          }

          // Get API URL from the same place ragApi uses it
          const apiUrl =
            window.env.REACT_APP_RAG_API_URL || "http://localhost:8002/api/v1";

          // Make direct fetch request with token
          const response = await fetch(
            `${apiUrl}/projects/?include_shared=true`,
            {
              method: "GET",
              headers: withAcceptLanguageHeader({
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
                ...getRequestScopeHeaders(),
              }),
            },
          );

          if (!response.ok) {
            throw new Error(
              `API error: ${response.status} ${response.statusText}`,
            );
          }

          const projects = await response.json();

          // Transform the API response to match the expected format
          const formattedProjects = projects.map((project) => ({
            id: project.id,
            name: project.name,
            description: project.description || "",
            isOwner: project.is_owner,
            accessLevel: normalizeProjectAccessLevel(project.access_level),
            createdAt: project.created_at,
            apProjectId: project.ap_project_id,
            organizationId: project.organization_id,
            userId: project.user_id,
          }));

          if (!isLatestRequest(requestId)) {
            return;
          }

          setAvailableProjects(formattedProjects);
          return;
        } else {
          // Re-throw if not an auth error
          throw firstError;
        }
      }
    } catch (err) {
      if (!isLatestRequest(requestId)) {
        return;
      }

      setError(err.message);
      // Only show error toast if we're authenticated (not on login page)
      if (isKeycloakAuthenticated()) {
        toast.error(
          i18n.t("projects.failedToLoadProjects", { message: err.message }),
        );
      }
    } finally {
      if (isLatestRequest(requestId)) {
        setLoading(false);
      }
    }
  }, []);

  // Fetch projects on component mount or when authentication succeeds
  useEffect(() => {
    // Check if already authenticated and fetch projects immediately
    if (keycloak && isKeycloakAuthenticated()) {
      fetchProjects();
    }

    // Set up Keycloak event listeners for login/logout
    if (keycloak) {
      // Store original handlers if they exist
      const originalAuthSuccess = keycloak.onAuthSuccess;
      const originalRefreshSuccess = keycloak.onAuthRefreshSuccess;

      // Set up our handlers
      keycloak.onAuthSuccess = () => {
        fetchProjects();
        // Call original handler if it exists
        if (originalAuthSuccess) originalAuthSuccess();
      };

      keycloak.onAuthRefreshSuccess = () => {
        // Call original handler if it exists
        if (originalRefreshSuccess) originalRefreshSuccess();
      };

      // Cleanup listeners on unmount
      return () => {
        keycloak.onAuthSuccess = originalAuthSuccess;
        keycloak.onAuthRefreshSuccess = originalRefreshSuccess;
      };
    }
  }, [fetchProjects]);

  // Listen for organization changes and refresh projects
  useEffect(() => {
    if (keycloak && isKeycloakAuthenticated()) {
      console.log(
        "Organization changed, refreshing projects...",
        currentOrganization,
      );
      setActiveProjectId(null);
      setSelectedProjects(["all"]);
      fetchProjects();
    }
  }, [currentOrganization, fetchProjects]);

  const toggleProject = (projectId) => {
    if (projectId === "all") {
      setActiveProjectId(null);
      setSelectedProjects(["all"]);
    } else {
      setSelectedProjects((prev) => {
        const newSelection = prev.includes(projectId)
          ? prev.filter((id) => id !== projectId)
          : [...prev.filter((id) => id !== "all"), projectId];
        const nextSelection =
          newSelection.length === 0 ? ["all"] : newSelection;
        setActiveProjectId(getActiveProjectIdFromSelection(nextSelection));
        return nextSelection;
      });
    }
  };

  const removeProject = (projectId) => {
    setSelectedProjects((prev) => {
      const newSelection = prev.filter((id) => id !== projectId);
      const nextSelection = newSelection.length === 0 ? ["all"] : newSelection;
      setActiveProjectId(getActiveProjectIdFromSelection(nextSelection));
      return nextSelection;
    });
  };

  const getProjectName = (projectId) => {
    const project = availableProjects.find((p) => p.id === projectId);
    return project ? project.name : "Unknown Project";
  };

  const createProject = async (projectData) => {
    try {
      setLoading(true);
      const newProject = await projectsApi.createProject({
        name: projectData.name,
        description: projectData.description || "",
        list_share_users: projectData.teamMembers || [],
      });

      // Add the new project to the available projects
      setAvailableProjects((prev) => [
        ...prev,
        {
          id: newProject.id,
          name: newProject.name,
          description: newProject.description || "",
          isOwner: true,
          accessLevel: "owner",
          createdAt: newProject.created_at,
        },
      ]);

      await invalidateProjectsQuery();

      toast.success(i18n.t("projects.projectCreatedSuccess"));
      return newProject;
    } catch (err) {
      toast.error(i18n.t("projects.failedToCreateProject"));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateProject = async (projectId, projectData) => {
    try {
      setLoading(true);
      const updatedProject = await projectsApi.updateProject(projectId, {
        name: projectData.name,
        description: projectData.description || "",
      });

      // Update the project in the available projects list
      setAvailableProjects((prev) =>
        prev.map((project) =>
          project.id === projectId
            ? {
                ...project,
                name: updatedProject.name,
                description: updatedProject.description || "",
              }
            : project,
        ),
      );

      await invalidateProjectsQuery();

      toast.success(i18n.t("projects.projectUpdatedSuccess"));
      return updatedProject;
    } catch (err) {
      toast.error(i18n.t("projects.failedToUpdateProject"));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteProject = async (projectId, permanent = false, force = false) => {
    try {
      setLoading(true);
      await projectsApi.deleteProject(projectId, permanent, force);

      // Remove the project from the available projects list
      setAvailableProjects((prev) =>
        prev.filter((project) => project.id !== projectId),
      );

      // If the project was selected, remove it from selected projects
      const safeSelectedProjects = Array.isArray(selectedProjects)
        ? selectedProjects
        : ["all"];
      if (safeSelectedProjects.includes(projectId)) {
        removeProject(projectId);
      }

      await invalidateProjectsQuery();

      toast.success(i18n.t("projects.projectDeletedSuccess"));
      return true;
    } catch (err) {
      toast.error(i18n.t("projects.failedToDeleteProject"));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getProjectDependencies = async (projectId) => {
    try {
      setLoading(true);
      const dependencies = await projectsApi.getProjectDependencies(projectId);
      return dependencies;
    } catch (err) {
      toast.error(
        i18n.t("projects.failedToGetDependencies", { message: err.message }),
      );
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const shareProject = async (projectId, email, accessLevel) => {
    try {
      setLoading(true);
      const shareResult = await projectsApi.shareProject(projectId, {
        email,
        access_level: accessLevel,
      });

      toast.success(i18n.t("projects.projectSharedSuccess", { email }));
      return shareResult;
    } catch (err) {
      toast.error(
        i18n.t("projects.failedToShareProject", { message: err.message }),
      );
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateShareAccess = async (projectId, shareId, accessLevel) => {
    try {
      setLoading(true);
      const updateResult = await projectsApi.updateProjectShare(
        projectId,
        shareId,
        {
          access_level: accessLevel,
        },
      );

      toast.success(i18n.t("projects.accessLevelUpdatedSuccess"));
      return updateResult;
    } catch (err) {
      toast.error(i18n.t("projects.failedToUpdateAccessLevel"));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const removeShare = async (projectId, shareId) => {
    try {
      setLoading(true);
      await projectsApi.deleteProjectShare(projectId, shareId);

      toast.success(i18n.t("projects.shareRemovedSuccess"));
      return true;
    } catch (err) {
      toast.error(i18n.t("projects.failedToRemoveShare"));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const refreshProjects = () => {
    fetchProjects();
  };

  return (
    <ProjectContext.Provider
      value={{
        selectedProjects,
        availableProjects,
        loading,
        error,
        toggleProject,
        removeProject,
        getProjectName,
        createProject,
        updateProject,
        deleteProject,
        getProjectDependencies,
        shareProject,
        updateShareAccess,
        removeShare,
        refreshProjects,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

export const useProjects = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error("useProjects must be used within a ProjectProvider");
  }
  return context;
};
